// Apple-style sequential configurator: exactly one step is active, every
// step after it is locked, every step before it is complete (and editable).

export type SequentialStepState = 'complete' | 'active' | 'locked';

export type SequentialStepInput<Id extends string = string> = {
  id: Id;
  /** Rendered on this page/region. Hidden steps never block the ones after them. */
  visible?: boolean;
  /** Answered, or carrying a safe default. Ignored for the terminal step. */
  complete?: boolean;
  /** The closing step (order summary). Active once everything before it is complete. */
  terminal?: boolean;
};

export type SequentialStepsResolution<Id extends string = string> = {
  activeId: Id | null;
  allComplete: boolean;
  states: Partial<Record<Id, SequentialStepState>>;
};

export function resolveSequentialSteps<Id extends string>(
  steps: readonly SequentialStepInput<Id>[],
): SequentialStepsResolution<Id> {
  const states: Partial<Record<Id, SequentialStepState>> = {};
  let activeId: Id | null = null;
  let allComplete = true;

  for (const step of steps) {
    if (step.visible === false) continue;
    if (activeId !== null) {
      // Strict sequence: everything after the active step is locked, even a
      // step that already carries a default (it shows the default pre-selected
      // and completes as soon as the flow reaches it).
      states[step.id] = 'locked';
      if (!step.terminal && !step.complete) allComplete = false;
      continue;
    }
    if (step.terminal || !step.complete) {
      states[step.id] = 'active';
      activeId = step.id;
      if (!step.terminal) allComplete = false;
      continue;
    }
    states[step.id] = 'complete';
  }

  return { activeId, allComplete, states };
}

/** Index of a step in the declared order; past the end for "all done". */
export function sequentialStepIndex<Id extends string>(
  steps: readonly SequentialStepInput<Id>[],
  id: Id | null,
): number {
  if (id === null) return steps.length;
  const index = steps.findIndex((step) => step.id === id);
  return index === -1 ? steps.length : index;
}
